import type { DocNode } from '@atlaskit/adf-schema/doc';
import { type PluginInvocationMessage } from '@atlassian/conversation-assistant-service-api/plugins';
import {
	type ConversationChannelStatus,
	type HumanMessage,
	type MessageFileId,
	type StreamAnswerPart,
	type StreamResponse,
	type StreamTrace,
} from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import { ApiError } from '@atlassian/conversation-assistant-service-api/utils/api-error';
import {
	createHumanMessage,
	getId,
	addAnswerPart as serviceAddAnswerPart,
	removeSensitiveData as serviceRemoveSensitiveData,
} from '@atlassian/conversation-assistant-service/services/assistance-service/utils/data';
import { EMPTY_CONVERSATION_ID } from '@atlassian/conversation-assistant-store/controllers/chat';
import { type StoreError } from '@atlassian/conversation-assistant-store/controllers/chat-signals';
import {
	CHAT_STORE_STATUS,
	type ChatStorePrivateState,
	type Conversation,
	type CsmMediaAttachment,
	isStoreAssistant,
	isStoreHuman,
	isStoreMessage,
	type MessageTemplate,
	type StoreAssistant,
	type StoreChatItem,
	type StoreHuman,
	type StoreMessage,
	type Trace,
} from '@atlassian/conversation-assistant-store/controllers/chat/types';

import type { OmniConversationItem } from '../../extensions/twilio/ocg-types';

import { StreamErrorException } from './main';
import { type OmniEnhancedConversation } from './types';

export const genIntId = (): string => String(Math.floor(Math.random() * 1000000));

export const getTraceFromStreamMessage = (streamMessage: StreamTrace): Trace => {
	return {
		messageContent: streamMessage.message?.content,
		messageTemplate: streamMessage.message?.message_template as MessageTemplate,
	};
};

export const createStoreError = (error: unknown, prefix: string = 'Store Error'): StoreError => {
	let status_code, message_template, timeout;
	let errorName: string | undefined;
	const message =
		error instanceof Error ? error.message : typeof error === 'string' ? error : 'Unknown error';
	const prefixedMessage = prefix ? `${prefix}: ${message}` : message;

	if (error instanceof StreamErrorException) {
		status_code = error.detail.message.status_code;
		message_template = error.detail.message.message_template;
		timeout = error.detail.metadata?.timeout;
		errorName = error.exceptionName;
	}

	if (error instanceof ApiError) {
		status_code = error.errorAttributes.statusCode;
		message_template = error.errorAttributes.errorCode;
	}

	return {
		type: 'ERROR',
		content: prefixedMessage,
		time_created: new Date().toLocaleString(),
		status_code,
		message_template,
		timeout,
		errorName,
		id: genIntId(),
	};
};

export const csmMediaAttachmentsFromFileIds = (fileIds: string[]): CsmMediaAttachment[] =>
	fileIds.map((id) => ({
		id,
		fileName: '',
		mimeType: '',
	}));

export const createStoreHumanWithContent = (
	content: HumanMessage,
	csmMediaAttachments?: CsmMediaAttachment[],
): StoreHuman => ({
	type: 'HUMAN',
	content,
	isHistorical: true,
	...(csmMediaAttachments && csmMediaAttachments.length > 0 ? { csmMediaAttachments } : {}),
});

export const createStoreHuman = ({
	id,
	content,
	isFromSeededConversation,
	requestId,
	fileUploads,
	csmMediaAttachments,
}: {
	id: string;
	content: string | DocNode;
	isFromSeededConversation?: boolean;
	requestId?: string;
	fileUploads?: MessageFileId[];
	csmMediaAttachments?: StoreHuman['csmMediaAttachments'];
}): StoreHuman => ({
	type: 'HUMAN',
	content: createHumanMessage(id, content, isFromSeededConversation, requestId, fileUploads),
	...(csmMediaAttachments && csmMediaAttachments.length > 0 ? { csmMediaAttachments } : {}),
});

export const createStoreHumanForTwilioLiveChat = (
	...args: Parameters<typeof createHumanMessage>
): StoreHuman => ({
	type: 'HUMAN',
	content: createHumanMessage(...args),
	source: 'TWILIO_LIVE_CHAT',
});

/**
 * Variant of {@link createStoreHumanForTwilioLiveChat} that also attaches
 * CSM Twilio MCS attachment metadata to the help-seeker's stored message.
 *
 * Used so that help-seeker–authored attachments survive page refresh: the
 * Twilio backfill path repopulates `csmTwilioAttachments` from the same
 * `Message.attachedMedia` data the realtime listener uses, keeping the
 * rendered bubble identical.
 *
 * Only call when the `csm-omni-channel-live-chat-attachments` feature gate
 * is on.
 */
export const createStoreHumanForTwilioLiveChatWithAttachments = (
	args: Parameters<typeof createHumanMessage>,
	csmTwilioAttachments: StoreHuman['csmTwilioAttachments'],
): StoreHuman => ({
	type: 'HUMAN',
	content: createHumanMessage(...args),
	source: 'TWILIO_LIVE_CHAT',
	...(csmTwilioAttachments && csmTwilioAttachments.length > 0 ? { csmTwilioAttachments } : {}),
});

export const createStoreAssistantWithContent = (content: StreamResponse): StoreAssistant => ({
	type: 'ASSISTANT',
	content,
});

export const createStoreAssistant = (args: {
	id: string;
	content: string | DocNode;
	agentNamedId?: string;
}): StoreAssistant => ({
	type: 'ASSISTANT',
	content: {
		type: 'FINAL_RESPONSE',
		message: {
			message: {
				role: 'ASSISTANT',
				id: args.id,
				content: args.content,
				time_created: new Date().toISOString(),
				user_ari: 'unknown',
				plugin_invocations: [],
				author: {
					named_id: args.agentNamedId || 'ai_mate_agent',
					name: 'unknown',
					actor_type: 'AGENT',
					ari: null,
					id: 'unknown',
				},
			},
		},
	},
});

const getStoreMessageId = (storeMessage: StoreMessage): string => {
	if (isStoreAssistant(storeMessage)) {
		return getId(storeMessage.content);
	}
	return storeMessage.content.id;
};

export const findStoreMessageIndex = (chat: StoreChatItem[], messageId?: string): number =>
	chat.findIndex((storeMessage) =>
		isStoreMessage(storeMessage) && messageId
			? getStoreMessageId(storeMessage) === messageId
			: true,
	);

const updateStoreMessage = (
	chat: StoreChatItem[],
	messageId: string,
	updateFunction: (storeMessage: StoreMessage) => StoreMessage,
	updateType: string,
	useIndex?: number,
): StoreChatItem[] => {
	// If the chat is empty, return it as is - prevents the 'in' of 'undefined' errors we were seeing
	// TODO: Take this into account with upcoming refactor
	if (!chat.length) {
		return chat;
	}

	const targetIndex = useIndex || findStoreMessageIndex(chat, messageId);
	const targetStoreMessage = chat[targetIndex];
	if (targetIndex < 0 || !isStoreMessage(targetStoreMessage)) {
		throw new Error(`Attempting to update a non StoreMessage (${updateType})`);
	}
	if (getStoreMessageId(targetStoreMessage) !== messageId) {
		throw new Error(`Attempting to update a store message at the wrong index (${updateType})`);
	}
	const newStoreMessage = updateFunction(targetStoreMessage);
	const newChat = [...chat];
	newChat.splice(targetIndex, 1, newStoreMessage);
	return newChat;
};

const updateStoreAssistant = (
	chat: StoreChatItem[],
	promptId: string,
	updateFunction: (storeMessage: StoreAssistant) => StoreAssistant,
	updateType: string,
	useIndex?: number,
): StoreChatItem[] =>
	updateStoreMessage(
		chat,
		promptId,
		(storeMessage): StoreAssistant => {
			if (!isStoreAssistant(storeMessage)) {
				throw new Error(`Attempting to update a non StoreAssistant (${updateType})`);
			}
			return updateFunction(storeMessage);
		},
		updateType,
		useIndex,
	);

export const addAnswerPart = (
	chat: StoreChatItem[],
	answerId: string,
	answerPart: StreamAnswerPart,
	useIndex?: number,
): StoreChatItem[] =>
	updateStoreAssistant(
		chat,
		answerId,
		(storeAssistant): StoreAssistant => ({
			...storeAssistant,
			content: {
				...serviceAddAnswerPart(storeAssistant.content, answerPart),
				streamingComplete: false,
			},
		}),
		'Add Answer Part',
		useIndex,
	);

export const replaceAnswerData = (
	chat: StoreChatItem[],
	answerId: string,
	content: StreamResponse,
	useIndex?: number,
): StoreChatItem[] =>
	updateStoreAssistant(
		chat,
		answerId,
		(storeMessage): StoreAssistant => ({
			...storeMessage,
			content: {
				...content,
				streamingComplete: false,
			},
		}),
		'Replace Answer Data',
		useIndex,
	);

export const markStreamingMessagesAsComplete = (chat: StoreChatItem[]): StoreChatItem[] =>
	chat.map((message) => {
		if (isStoreAssistant(message) && !message.content.streamingComplete) {
			return {
				...message,
				content: { ...message.content, streamingComplete: true },
			};
		}
		return message;
	});

export const setIsAborted = (
	chat: StoreChatItem[],
	messageId: string,
	isAborted: boolean,
	useIndex?: number,
): StoreChatItem[] =>
	updateStoreAssistant(
		chat,
		messageId,
		(storeMessage): StoreAssistant => ({
			...storeMessage,
			isAborted,
		}),
		'Set feedback availability',
		useIndex,
	);

/**
 * Returns the message pointed by messageId plus the number of previous messages set by messageCount.
 * If @param startFromMessageId is not provided, the last n messages are returned.
 * Example: messageCount = 1 => array with the target message plus 1 previous message.
 */
export const getMessageHistory = (
	chat: StoreChatItem[],
	startFromMessageId?: string,
	messageCount: number = 1,
): StoreChatItem[] => {
	const messageIndex = findStoreMessageIndex(chat, startFromMessageId);
	if (messageIndex < 0) {
		return [];
	}
	const messageHistory = chat.slice(Math.max(0, messageIndex - messageCount), messageIndex + 1);

	return messageHistory;
};

export const clearStatus = (): Partial<Conversation> => ({
	status: CHAT_STORE_STATUS.idle,
	traces: [],
	storeAbortController: undefined,
});

export const removeSensitiveData = (message: StoreAssistant): StoreAssistant => ({
	...message,
	content: serviceRemoveSensitiveData(message.content),
});

export const getConversation = (
	state: ChatStorePrivateState,
	id: string,
): Conversation | undefined => {
	return state.conversations[id];
};

export const filterAndSortConversations = (
	conversations: Conversation[],
	filters: {
		searchValue: string;
		direction: 'asc' | 'desc';
	},
): Conversation[] => {
	return conversations
		.filter(
			(conversation) =>
				!!conversation &&
				!isEmptyConversation(conversation.id) &&
				(conversation.name || '')
					.toLocaleLowerCase()
					.includes(filters.searchValue.toLocaleLowerCase()),
		)
		.sort((a, b) => {
			if (!a.lastMessageTimestamp) {
				return 1;
			}
			if (!b.lastMessageTimestamp) {
				return -1;
			}
			if (filters.direction === 'asc') {
				return (
					new Date(a.lastMessageTimestamp).getTime() - new Date(b.lastMessageTimestamp).getTime()
				);
			} else {
				return (
					new Date(b.lastMessageTimestamp).getTime() - new Date(a.lastMessageTimestamp).getTime()
				);
			}
		});
};

export const checkActiveConversationMatchesSource = (
	state: ChatStorePrivateState,
	id?: string,
): boolean => {
	return !!id && state.conversations[state.currentConversationId]?.sourceId === id;
};

/**
 * Returns true if the conversation is a direct live chat (no AI involvement).
 * Created via POST /conversation/livechat
 */
export const isDirectLiveChat = (conv: OmniEnhancedConversation | undefined): boolean =>
	conv?.convoAIConversationId == null && conv?.twilioConversationId != null;

const VALID_CHANNEL_STATUSES = new Set<string>(['OPEN', 'ENDED', 'EXPIRED', 'ESCALATED']);

/**
 * Maps an omni-core conversation item (from GET /api/v1/omni/conversation) to the
 * store's Conversation shape. The store key is derived as:
 *   convoAiConversationId ?? twilioConversationId
 *
 * Returns null if neither ID is present (should not happen in practice).
 */
export const mapOmniConversationToStore = (
	item: OmniConversationItem,
): OmniEnhancedConversation | null => {
	const key = item.convoAiConversationId ?? item.twilioConversationId;
	if (!key) {
		return null;
	}

	const convoAiResponse = item.convoAiConversationResponse;

	const rawStatus = item.status ?? convoAiResponse?.status;
	const channelStatus =
		rawStatus && VALID_CHANNEL_STATUSES.has(rawStatus)
			? (rawStatus as ConversationChannelStatus)
			: undefined;

	const timeCreated = item.createdAt ?? convoAiResponse?.time_created ?? '';

	return {
		id: key,
		name: (item.name || convoAiResponse?.name || '') as string,
		timeCreated: timeCreated,
		lastMessageTimestamp:
			item.lastMessageAt ?? convoAiResponse?.last_message_timestamp ?? timeCreated,
		messages: [],
		status: CHAT_STORE_STATUS.idle,
		traces: [],
		channelStatus,
		convoAIConversationId: item.convoAiConversationId,
		twilioConversationId: item.twilioConversationId,
		interactionId: item.interactionId,
		omniProcessed: item.omniProcessed,
	};
};

export const getLastHumanMessageFromConversation = (
	conversation: Conversation,
): StoreHuman | undefined => {
	return findLast(conversation?.messages, isStoreHuman);
};

export const getLastRequestIdFromConversation = (
	conversation: Conversation,
): string | undefined => {
	return getLastHumanMessageFromConversation(conversation)?.content.requestId;
};

export const getAgentMessageById = (
	messages: StoreChatItem[],
	messageId: string,
): { result: StoreAssistant | false; index: number } => {
	let index = -1;
	const result = messages.find((item, i) => {
		index = i;
		return isStoreAssistant(item) && getId(item.content) === messageId;
	});
	return result
		? {
				result: result as StoreAssistant,
				index,
			}
		: { result: false, index };
};

// Request ID is used as the most consistent identifying value for the E2E life cycle of a message
// Since we don't have the true network messageId until the FINAL_RESPONSE from the stream
// This allows us to identify the funnel of events from message send through to abort, dismiss, error, success, etc
export const addRequestIdToLatestHumanMessage = (
	chat: StoreChatItem[],
	requestId?: string,
): StoreChatItem[] => {
	const lastHumanMessage = findLast(chat, isStoreHuman);

	// Skip if there is no last human message or if the requestId is already set
	if (!lastHumanMessage || !requestId || !!lastHumanMessage.content.requestId) {
		return chat;
	}
	const newHumanMessage = {
		...lastHumanMessage,
		content: {
			...lastHumanMessage.content,
			requestId,
		},
	};
	const newChat = [...chat];
	newChat[newChat.lastIndexOf(lastHumanMessage)] = newHumanMessage;
	return newChat;
};

export const isEmptyConversation = (conversationId?: string): boolean =>
	conversationId === EMPTY_CONVERSATION_ID || conversationId === undefined;

export const isMessageLastOfType = (
	chat: StoreChatItem[],
	type: StoreChatItem['type'],
	index: number,
): boolean => {
	const lastIndex = chat.map((item) => item.type).lastIndexOf(type);
	return lastIndex === index;
};

export const getFirstMessageOfType = <T extends StoreChatItem>(
	chat: StoreChatItem[],
	type: T['type'],
): T | undefined => {
	const lastIndex = chat.map((item) => item.type).indexOf(type);

	if (lastIndex >= 0) {
		return chat[lastIndex] as T | undefined;
	}
};

// findLast polyfill since it's a newer ECMAScript feature
// https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/findLast
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const findLast = (array: any[], predicate: (item: any) => any): any => {
	for (let i = array.length - 1; i >= 0; i--) {
		if (predicate(array[i])) {
			return array[i];
		}
	}
	return undefined;
};

// Request ID is used as the most consistent identifying value for the E2E life cycle of a message
// Since we don't have the true network messageId until the FINAL_RESPONSE from the stream
// This allows us to identify the funnel of events from message send through to abort, dismiss, error, success, etc
export const applyRedactionToLatestHumanMessage = (
	chat: StoreChatItem[],
	redactedPrompt: string,
): StoreChatItem[] => {
	const lastHumanMessage = findLast(chat, isStoreHuman);
	if (!lastHumanMessage) {
		return chat;
	}
	const updated = { ...lastHumanMessage, redactedPrompt };
	const newChat = [...chat];
	newChat[newChat.lastIndexOf(lastHumanMessage)] = updated;
	return newChat;
};

export const applyRedactionToLatestAssistantMessage = (
	chat: StoreChatItem[],
	redactedResponse: string,
): StoreChatItem[] => {
	const lastAssistantMessage = findLast(chat, isStoreAssistant);
	if (!lastAssistantMessage) {
		return chat;
	}
	const updated = { ...lastAssistantMessage, redactedResponse };
	const newChat = [...chat];
	newChat[newChat.lastIndexOf(lastAssistantMessage)] = updated;
	return newChat;
};

export const getSourceAgentId = (
	state: ChatStorePrivateState,
	conversationId: string,
): string | undefined => {
	const conversation = getConversation(state, conversationId);
	const assistantMessage = conversation?.messages.find((message) => isStoreAssistant(message)) as
		| StoreAssistant
		| undefined;

	return assistantMessage?.content.message.message.author_id ?? conversation?.sourceAgentId;
};

const FRUSTRATION_DETECTION_PLUGIN_NAME = 'Frustration-Detection-Plugin';

const ESCALATION_PLUGINS = new Set([
	'Ticket-Filling-Plugin',
	'create-request',
	FRUSTRATION_DETECTION_PLUGIN_NAME,
]);

/*
 * hasIssue and hasEscalationPluginWithExtraInfo are used to infer if handover has completed.
 * During handover plugin_extra_info will transition from null to a JSON string.
 * Only when plugin_extra_info.issueKey is not null will we assume that handover has completed.
 */
export const hasIssueKey = (plugin_extra_info: string): boolean => {
	try {
		const { issueKey } = JSON.parse(plugin_extra_info);
		return Boolean(issueKey);
	} catch {
		return false;
	}
};

export const hasEscalationPluginWithExtraInfo = (msgs: PluginInvocationMessage[]): boolean =>
	msgs.some(
		({ plugin_name, plugin_extra_info }) =>
			plugin_name &&
			ESCALATION_PLUGINS.has(plugin_name) &&
			plugin_extra_info &&
			hasIssueKey(plugin_extra_info),
	);

/**
 * Parsed representation of the LiveChatPlugin's `plugin_extra_info` JSON,
 * produced by the backend's {@link LiveChatPluginOutput}.
 */
export type LiveChatPluginExtraInfo = {
	liveChatConversationId: string;
	/**
	 * Present (and `true`) on the {@link FRUSTRATION_DETECTION_PLUGIN_NAME}
	 * variant of the contract — see {@link getLiveChatPluginExtraInfo}.
	 */
	isLiveChatHandoff?: boolean;
};

const LIVE_CHAT_HANDOFF_TOOL_PLUGIN_NAME = 'LiveChatHandoffTool';

/**
 * Extracts the {@link LiveChatPluginExtraInfo} from the first plugin invocation
 * that signals a successful live-chat handoff.
 *
 * Two contracts are recognised:
 *
 * 1. `plugin_name === 'LiveChatHandoffTool'` — any invocation whose
 *    `plugin_extra_info` contains a valid `liveChatConversationId`.
 * 2. `plugin_name === 'Frustration-Detection-Plugin'` — only when its
 *    `plugin_extra_info` contains BOTH `isLiveChatHandoff: true` AND a valid
 *    `liveChatConversationId`. Frustration-Detection invocations that do not
 *    flip `isLiveChatHandoff` (e.g. the Ticket-Filling escalation contract)
 *    are intentionally ignored here.
 *
 * Returns `null` when no matching invocation is found.
 */
export const getLiveChatPluginExtraInfo = (
	msgs: PluginInvocationMessage[],
): LiveChatPluginExtraInfo | null => {
	for (const { plugin_name, plugin_extra_info } of msgs) {
		if (!plugin_extra_info) {
			continue;
		}
		const isLiveChatHandoffTool = plugin_name === LIVE_CHAT_HANDOFF_TOOL_PLUGIN_NAME;
		const isFrustrationDetection = plugin_name === FRUSTRATION_DETECTION_PLUGIN_NAME;
		if (!isLiveChatHandoffTool && !isFrustrationDetection) {
			continue;
		}
		try {
			const parsed = JSON.parse(plugin_extra_info) as Partial<LiveChatPluginExtraInfo>;
			if (!parsed.liveChatConversationId) {
				continue;
			}
			// For the Frustration-Detection-Plugin contract, only treat the
			// invocation as a live-chat handoff signal when the `isLiveChatHandoff`
			// flag is explicitly set to `true`.
			if (isFrustrationDetection && parsed.isLiveChatHandoff !== true) {
				continue;
			}
			return parsed as LiveChatPluginExtraInfo;
		} catch {
			// Malformed JSON – skip this invocation
		}
	}
	return null;
};

// Helper function to detect Service Desk (JSM) sources
const serviceDeskSourceRegex = /https:\/\/[a-zA-Z0-9_.-]+\/servicedesk\/customer\//g;

export const containsServiceDeskSource = (pluginOutput: string): boolean => {
	return serviceDeskSourceRegex.test(pluginOutput);
};

// Used to generate a unique `singleInstrumentationID` for tracking Question & Answer pairings
// before a response has been received from the server
export const djb2Hash = (timeStamp: string, conversationId: string): number => {
	// More detail on djb2 hash function https://theartincode.stanis.me/008-djb2/
	let hash = 5381;

	if (!timeStamp || !conversationId) {
		return hash;
	}

	const concat = conversationId + timeStamp;

	for (let i = 0; i < concat.length; i++) {
		// Update the hash value
		hash = (hash << 5) + hash + concat.charCodeAt(i); // hash * 33 + c
	}
	// Ensure the hash is a positive integer
	return hash >>> 0;
};

export const generateTmpMessageId = (conversationId: string): string =>
	String(djb2Hash(new Date().toISOString(), conversationId));
